import React, { useEffect, useRef, useState } from "react";
import GameCanvas from "../../../game-canvas/GameCanvas";
import { wormColors, powerUpDefinitions } from "core/src/core/constants.js";
import * as cf from "core/src/core/core-functions.js";
import { POWERUPS, POWERUP_BADGE } from "../../../shared/powerups";
import { createHarnessBus, type HarnessBus, type NetConditions } from "./harness-bus";
import { createHarnessServer, type HarnessServer } from "./harness-server";
import { createHarnessClient, type HarnessClient } from "./harness-client";
import { createAiDriver } from "./ai-driver";

// DEBUG-ONLY route /debug/net. Server + two client-authoritative clients in this
// page, over an in-process network with tunable latency/jitter/loss. Five views:
//   [ Client 1 truth ] [ Server truth ] [ Client 2 truth ]
//   [ Client 1 smooth ] [     -       ] [ Client 2 smooth ]
// truth = latest state, no render delay; smooth = normal client render.
// Client 1 = you (arrow keys / A-D) or a bot; Client 2 = a real AI bot.
//
// RECORD & REPLAY (faithful, wall-clock): "Record" grabs the actual pixels of all
// five canvases (+ the diagnostics row) every frame. Playback steps through those
// frames, so you see EXACTLY what each screen showed at the same earth-instant -
// the lag included - not a reconstructed/converged view. Frame-step, play, scrub.

// Canvas + map bumped (380 -> 460) since the harness now runs 4-5 worms (C1's two
// local players + C2 + a server bot, plus twins): more play area and more pixels
// per view so a crowded board stays readable.
const CANVAS = 460;
const MAP_SIZE = 460;
const CAP_MS = 40; // capture interval (~25 fps)
const CAP_MAX = 2000; // frame cap (~80s) to bound memory
// Only power-ups that can actually spawn (weight > 0). Excludes ones removed from the game for now
// (weightedSpawnChance 0, e.g. clear + the green self wall hack) - toggling them here would do
// nothing since the spawner never picks them. Restore a core weight and it reappears automatically.
const ALL_POWERUP_KEYS: string[] = Object.keys(powerUpDefinitions).filter(
  (k) => ((powerUpDefinitions as any)[k].weightedSpawnChance || 0) > 0
);
// key -> {name, icon, color} so the selector can show the same coloured icon badges as
// the Host / Local Game power-up grid (green=self, red=others, blue=all), kept small.
const PU_META: Record<string, { name: string; icon: string; color: "green" | "red" | "blue" }> =
  Object.fromEntries(POWERUPS.map((p) => [p.key, p]));
// Selector order: green (self), then red (others), then blue (all) - POWERUPS is already
// grouped that way. Any core key missing from the catalog is appended so none is dropped.
const ORDERED_PU_KEYS: string[] = [
  ...POWERUPS.map((p) => p.key).filter((k) => ALL_POWERUP_KEYS.includes(k)),
  ...ALL_POWERUP_KEYS.filter((k) => !PU_META[k]),
];
const HARNESS_SPAWN_CHANCE = 0.45; // boosted vs the 0.12 default so power-ups appear fast for testing

// Network conditions live in the URL (?ping=&jitter=&loss=) so they survive a
// round restart / reload and are shareable. Read on load, written live on change.
function readNetParams(): NetConditions {
  const q = new URLSearchParams(window.location.search);
  const num = (k: string, d: number) => { const v = Number(q.get(k)); return q.has(k) && isFinite(v) && v >= 0 ? v : d; };
  return { ping: num("ping", 120), jitter: num("jitter", 0), loss: num("loss", 0) };
}
// Debug: seed effects on every worm from round start, via ?startfx=type:strength:seconds (comma
// separated for several). Fat everyone for 30s to test twin-while-fat: ?startfx=size:2:30
// (thin: size:0.5:30, fast: speed:2:30, ...).
function readStartFx(): { type: string; strength: number; seconds: number }[] {
  const raw = new URLSearchParams(window.location.search).get("startfx");
  if (!raw) return [];
  return raw.split(",").map((s) => {
    const [type, strength, seconds] = s.split(":");
    return { type, strength: Number(strength), seconds: Number(seconds) };
  }).filter((e) => e.type && isFinite(e.strength) && isFinite(e.seconds) && e.seconds > 0);
}
function writeNetParam(k: string, v: number) {
  const q = new URLSearchParams(window.location.search);
  q.set(k, String(v));
  window.history.replaceState(null, "", window.location.pathname + "?" + q.toString());
}
// Enabled power-ups persist in ?powerups= so a reset / reload keeps your selection.
// Absent = all (clean URL); "none" = all off; else a CSV of powerUpDefinitions keys.
function readPowerupsParam(): string[] {
  const q = new URLSearchParams(window.location.search);
  if (!q.has("powerups")) return ALL_POWERUP_KEYS;
  const raw = q.get("powerups") || "";
  if (raw === "none") return [];
  return raw.split(",").filter((k) => ALL_POWERUP_KEYS.indexOf(k) !== -1);
}
function writePowerupsParam(keys: string[]) {
  const q = new URLSearchParams(window.location.search);
  if (keys.length === ALL_POWERUP_KEYS.length) q.delete("powerups");
  else q.set("powerups", keys.length ? keys.join(",") : "none");
  const qs = q.toString();
  window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
}

// One captured wall-clock frame: 5 panel images + the diagnostics rows at that instant.
interface Frame { images: string[]; info: InfoSnap | null; t: number; logLen: number } // logLen = wire messages sent by this frame
interface DiagRow { name: string; color: string; alive: (boolean | undefined)[]; disagree: boolean; svT: number; lag1: number; lag2: number; counts: [number, number, number]; content: string; contentBad: boolean }

// Live game-time readout for a canvas (each copy renders the world at its own clock).
function LiveClock({ get }: { get: () => number }) {
  const [t, setT] = useState(0);
  useEffect(() => { const id = setInterval(() => setT(get()), 100); return () => clearInterval(id); }, []);
  return <span style={{ marginLeft: "auto", color: "#8995b3", fontWeight: 600, fontVariantNumeric: "tabular-nums", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11.5 }}>t={t.toFixed(2)}s</span>;
}

// A hovered coordinate, optionally scoped to one owner so its crosshair only lights up
// that owner's canvases (C1 -> left pair, SV -> center, C2 -> right pair). No owner = all.
type HoverOwner = "c1" | "sv" | "c2";
type HoverPt = { x: number; y: number; owner?: HoverOwner };

// A gaming-style crosshair drawn over a canvas at a pixel coordinate: neon ring + center
// pip + four outer ticks with a center gap, softly glowing. Marks a hovered event's spot.
function CrossHair({ px, py }: { px: number; py: number }) {
  const S = 34, c = S / 2, col = "#39ff14";
  return (
    <svg width={S} height={S} viewBox={`0 0 ${S} ${S}`} style={{ position: "absolute", left: px - c, top: py - c, pointerEvents: "none", zIndex: 1000, filter: `drop-shadow(0 0 3px ${col})` }}>
      <circle cx={c} cy={c} r={9} fill="none" stroke={col} strokeWidth={1.3} opacity={0.85} />
      <circle cx={c} cy={c} r={1.5} fill={col} />
      <line x1={c} y1={0.5} x2={c} y2={7} stroke={col} strokeWidth={1.6} />
      <line x1={c} y1={S - 0.5} x2={c} y2={S - 7} stroke={col} strokeWidth={1.6} />
      <line x1={0.5} y1={c} x2={7} y2={c} stroke={col} strokeWidth={1.6} />
      <line x1={S - 0.5} y1={c} x2={S - 7} y2={c} stroke={col} strokeWidth={1.6} />
    </svg>
  );
}

// Overlay for a set of worm-path segments hovered in the WireLog. Each segment is a solid yellow
// line (green start dot -> red end dot); the GAP between one segment's end and the next's start is a
// dashed magenta line. So a healthy continuous trail reads as one solid chain, while a bulk-resend of
// a sparse sample (the switcharoonie bug) reads as short dashes with big dashed-magenta gaps.
function SegOverlay({ segs, g2p }: { segs: any[]; g2p: (v: number) => number }) {
  return (
    <svg width={CANVAS} height={CANVAS} style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", zIndex: 999 }}>
      {segs.map((s, i) => {
        if (s == null || s.startX == null) return null;
        const p = i > 0 ? segs[i - 1] : null;
        const gap = p && p.endX != null ? Math.hypot((s.startX || 0) - (p.endX || 0), (s.startY || 0) - (p.endY || 0)) : 0;
        return (
          <g key={i}>
            {gap > 0.5 ? <line x1={g2p(p!.endX)} y1={g2p(p!.endY)} x2={g2p(s.startX)} y2={g2p(s.startY)} stroke="#ff2fd0" strokeWidth={1.2} strokeDasharray="3 3" opacity={0.85} /> : null}
            <line x1={g2p(s.startX)} y1={g2p(s.startY)} x2={g2p(s.endX)} y2={g2p(s.endY)} stroke="#ffdf3a" strokeWidth={2.2} opacity={0.95} />
            <circle cx={g2p(s.startX)} cy={g2p(s.startY)} r={2.3} fill="#39ff14" />
            <circle cx={g2p(s.endX)} cy={g2p(s.endY)} r={2.3} fill="#ff5c7a" />
          </g>
        );
      })}
    </svg>
  );
}

// Switcharoonie state per view: read the client's predictors (any local worm's hold) or the host's phase.
function clientSwitchStatus(c: HarnessClient): "switching" | "frozen" | null {
  for (const pid of c.localIds()) {
    const pr = (c.predictors() as any)[pid];
    const s = pr && pr.switchStatus ? pr.switchStatus() : null;
    if (s === "frozen" || s === "switching") return s;
  }
  return null;
}
function serverSwitchStatus(s: HarnessServer): "switching" | "frozen" | null {
  const ph = (s.getServerGameState() as any)?._switchPhase;
  return ph === "frozen" ? "frozen" : ph ? "switching" : null;
}
// Switcharoonie freeze state now shows in the info panel's "Worms per owner" section (per view,
// C1/SV/C2), recorded into each frame's snapshot so it replays too - see clientSwitchStatus /
// serverSwitchStatus feeding OwnerList.switch in buildSnap.
function Panel({ title, subtitle, accent, boxRef, getTime, mapWidth = 400, highlight, segHi, owner, badge, children }: { title: string; subtitle?: string; accent?: string; boxRef?: React.Ref<HTMLDivElement>; getTime?: () => number; mapWidth?: number; highlight?: HoverPt | null; segHi?: any[] | null; owner?: HoverOwner; badge?: React.ReactNode; children?: React.ReactNode }) {
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const g2p = (v: number) => v * (CANVAS / mapWidth); // game coord -> box pixel
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "baseline", fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 0.5, color: accent || "#cbd3e6" }}>
        <span>{title}{subtitle ? <span style={{ color: "#6b7590", fontWeight: 600 }}> · {subtitle}</span> : null}</span>
        {badge}
        {getTime ? <LiveClock get={getTime} /> : null}
      </div>
      <div
        ref={boxRef}
        onMouseMove={(e) => { const b = e.currentTarget.getBoundingClientRect(); setHover({ x: (e.clientX - b.left) * (mapWidth / CANVAS), y: (e.clientY - b.top) * (mapWidth / CANVAS) }); }}
        onMouseLeave={() => setHover(null)}
        style={{ width: CANVAS, height: CANVAS, background: "#070a12", border: `1px solid ${accent ? accent + "55" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, overflow: "hidden", position: "relative" }}
      >
        {children}
        {hover ? <div style={{ position: "absolute", top: 4, left: 6, padding: "1px 5px", borderRadius: 4, background: "rgba(0,0,0,0.65)", color: "#c4ccdd", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, pointerEvents: "none", zIndex: 1000 }}>{hover.x | 0}, {hover.y | 0}</div> : null}
        {highlight && (!highlight.owner || highlight.owner === owner) ? <CrossHair px={g2p(highlight.x)} py={g2p(highlight.y)} /> : null}
        {segHi && segHi.length ? <SegOverlay segs={segHi} g2p={g2p} /> : null}
      </div>
    </div>
  );
}

function View(props: {
  gameState: any; players: any[];
  renderTime?: () => number;
  localPlayerIds?: string[]; predictors?: Record<string, any>;
  getTargetTime?: () => number; adaptive?: boolean;
  serverGhost?: { getSegment: (id: any) => any; getTime: () => number };
  powerUpTruthTime?: () => number;
}) {
  if (!props.gameState) return null;
  return (
    <GameCanvas
      config={{ width: CANVAS, height: CANVAS }}
      gameState={props.gameState}
      players={props.players}
      renderTime={props.renderTime}
      localPlayerIds={props.localPlayerIds}
      predictors={props.predictors}
      getTargetTime={props.getTargetTime}
      adaptiveDelay={!!props.adaptive}
      serverGhost={props.serverGhost}
      powerUpTruthTime={props.powerUpTruthTime}
    />
  );
}

function aliveOf(gs: any, playerId: string): boolean | undefined {
  if (!gs || !gs.worms) return undefined;
  const w = gs.worms.find((x: any) => x.playerId === playerId);
  return w ? w.alive !== false : undefined;
}
function trackFor(gs: any, playerId: string): any[] | null {
  if (!gs || !gs.wormPathSegments) return null;
  for (const k in gs.wormPathSegments) {
    const arr = gs.wormPathSegments[k];
    if (arr && arr.length && arr[0].playerId === playerId) return arr;
  }
  return null;
}
function lastTime(arr: any[] | null): number {
  if (!arr || !arr.length) return 0;
  const s = arr[arr.length - 1];
  return (s.startTime || 0) + (s.duration || 0);
}
// First segment index where two paths diverge in geometry/jump (or one runs short).
// -1 = identical. Compares a client's copy of an OPPONENT vs the server's copy.
function firstDiff(a: any[] | null, b: any[] | null): number {
  const n = Math.max(a ? a.length : 0, b ? b.length : 0);
  for (let i = 0; i < n; i++) {
    const x = a && a[i], y = b && b[i];
    if (!x || !y) return i;
    if (Math.round(x.endX) !== Math.round(y.endX) || Math.round(x.endY) !== Math.round(y.endY) || (x.jump ? 1 : 0) !== (y.jump ? 1 : 0)) return i;
  }
  return -1;
}

function computeDiag(server: any, c1: any, c2: any, players: any[], c1Ids: string[], c2Ids: string[]): DiagRow[] {
  return players.map((p) => {
    // Dot order matches the canvas layout: Client 1 · Server · Client 2.
    const alive = [aliveOf(c1, p.id), aliveOf(server, p.id), aliveOf(c2, p.id)];
    const known = alive.filter((v) => v !== undefined);
    const disagree = known.length > 1 && known.some((v) => v !== known[0]);
    const tSv = trackFor(server, p.id), t1 = trackFor(c1, p.id), t2 = trackFor(c2, p.id);
    const svT = lastTime(tSv);
    // A client can carry several local players; any of them makes the row that client's.
    const owner = c1Ids.indexOf(p.id) !== -1 ? "c1" : c2Ids.indexOf(p.id) !== -1 ? "c2" : null;
    const oppTrack = owner === "c1" ? t2 : owner === "c2" ? t1 : null;
    const oppName = owner === "c1" ? "c2" : "c1";
    const diff = oppTrack ? firstDiff(oppTrack, tSv) : -1;
    const contentBad = diff !== -1;
    return {
      name: p.name, color: wormColors[p.colorId], alive, disagree, svT,
      lag1: lastTime(t1) - svT, lag2: lastTime(t2) - svT,
      // c1 / sv / c2, matching the alive-dot column order (and the C1 · Server · C2 layout).
      counts: [t1 ? t1.length : 0, tSv ? tSv.length : 0, t2 ? t2.length : 0],
      content: owner ? (contentBad ? `${oppName} diverges @${diff}` : "match") : "-",
      contentBad,
    };
  });
}

function DiagTable({ rows }: { rows: DiagRow[] }) {
  const dot = (alive: boolean | undefined) => (
    <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: alive === undefined ? "#333a4a" : alive ? "#7CFC66" : "#ff4d5e" }} />
  );
  const cell: React.CSSProperties = { padding: "3px 12px", fontVariantNumeric: "tabular-nums" };
  const head: React.CSSProperties = { ...cell, color: "#6b7590", textAlign: "left", fontWeight: 600 };
  return (
    <table style={{ borderCollapse: "collapse", fontFamily: "'Rajdhani',sans-serif", fontSize: 13.5 }}>
      <thead>
        <tr><th style={head}>Worm</th><th style={head}>alive (c1·sv·c2)</th><th style={head}>known-t sv</th><th style={head}>c1 lag</th><th style={head}>c2 lag</th><th style={head}>segs c1/sv/c2</th><th style={head}>content vs server</th></tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ background: r.disagree || r.contentBad ? "rgba(255,159,69,0.12)" : "transparent" }}>
            <td style={{ ...cell, color: r.color, fontWeight: 700 }}>{r.name}</td>
            <td style={cell}>{dot(r.alive[0])} {dot(r.alive[1])} {dot(r.alive[2])} {r.disagree ? <span style={{ color: "#ff9f45", fontWeight: 700 }}>DISAGREE</span> : null}</td>
            <td style={cell}>{r.svT.toFixed(2)}s</td>
            <td style={{ ...cell, color: r.lag1 < -0.03 ? "#ffcf7a" : "#6b7590" }}>{r.lag1 >= 0 ? "+" : ""}{r.lag1.toFixed(2)}s</td>
            <td style={{ ...cell, color: r.lag2 < -0.03 ? "#ffcf7a" : "#6b7590" }}>{r.lag2 >= 0 ? "+" : ""}{r.lag2.toFixed(2)}s</td>
            <td style={cell}>{r.counts[0]} / {r.counts[1]} / {r.counts[2]}</td>
            <td style={{ ...cell, color: r.contentBad ? "#ff4d5e" : "#7CFC66", fontWeight: 700 }}>{r.content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Composite a panel's stacked canvases into one JPEG data URL (what that screen shows).
// Which of the five live game views render. Each mounted view is a full GameCanvas
// with its own rAF loop, and five at once is too heavy for some machines - so views
// can be switched off (the canvas unmounts entirely; a same-size placeholder keeps
// the grid from shifting). Persisted so the choice survives reloads.
const VIEWS = [
  { key: "c1t", title: "Client 1", subtitle: "truth", accent: "#4aa3ff" },
  { key: "svt", title: "Server", subtitle: "truth", accent: "#7CFC66" },
  { key: "c2t", title: "Client 2", subtitle: "truth", accent: "#ff5c7a" },
  { key: "c1s", title: "Client 1", subtitle: "smooth", accent: "#4aa3ff" },
  { key: "c2s", title: "Client 2", subtitle: "smooth", accent: "#ff5c7a" },
] as const;
type ViewKey = (typeof VIEWS)[number]["key"];
const VIEWS_KEY = "ar.netViews";
function readViews(): Record<ViewKey, boolean> {
  const all: Record<ViewKey, boolean> = { c1t: true, svt: true, c2t: true, c1s: true, c2s: true };
  try {
    const saved = JSON.parse(localStorage.getItem(VIEWS_KEY) || "{}");
    for (const v of VIEWS) if (typeof saved[v.key] === "boolean") all[v.key] = saved[v.key];
  } catch { /* fresh */ }
  return all;
}

// Render delay for BOTH harness clients' smooth views (ms). Seeded at client
// creation, driven live by the delay slider, persisted across reloads.
const DELAY_KEY = "ar.netDelay";
function readDelayMs(): number {
  try {
    const raw = localStorage.getItem(DELAY_KEY);
    if (raw !== null) {
      const v = Number(raw);
      if (isFinite(v) && v >= 0 && v <= 400) return v;
    }
  } catch { /* fresh */ }
  return 100;
}

// Fixed-footprint stand-in for a switched-off view: same title row + box size as
// Panel, so toggling views never reflows the grid.
function OffPanel({ title, subtitle, accent }: { title: string; subtitle?: string; accent?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: 0.4 }}>
      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 0.5, color: accent || "#cbd3e6" }}>
        {title}{subtitle ? <span style={{ color: "#6b7590", fontWeight: 600 }}> · {subtitle}</span> : null}
        <span style={{ color: "#6b7590", fontWeight: 600 }}> · off</span>
      </div>
      <div style={{ width: CANVAS, height: CANVAS, background: "#070a12", border: "1px dashed rgba(255,255,255,0.12)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#3a4157", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 12 }}>
        view off
      </div>
    </div>
  );
}

function capturePanel(box: HTMLDivElement | null): string {
  if (!box) return "";
  const off = document.createElement("canvas");
  off.width = CANVAS; off.height = CANVAS;
  const ctx = off.getContext("2d")!;
  ctx.fillStyle = "#070a12";
  ctx.fillRect(0, 0, CANVAS, CANVAS);
  box.querySelectorAll("canvas").forEach((c) => { try { ctx.drawImage(c, 0, 0, CANVAS, CANVAS); } catch { /* tainted/empty */ } });
  return off.toDataURL("image/jpeg", 0.65);
}

export default function DebugNetPage() {
  const [ready, setReady] = useState<{ bus: HarnessBus; server: HarnessServer; c1: HarnessClient; c2: HarnessClient; players: any[] } | null>(null);
  const condRef = useRef<NetConditions | null>(null);
  const [, force] = useState(0);
  const [autoC1, setAutoC1] = useState(false);
  // Both clients' render delay, adjustable live (mirrors the in-game admin slider).
  const [delayMs, setDelayMs] = useState<number>(readDelayMs);
  // Adaptive per-opponent clocks in the smooth views (production behavior). NOTE:
  // adaptive renders each opponent no fresher than its data-freshness floor
  // (one-way latency + batching + the 80ms margin), so the DELAY slider only has
  // an effect ABOVE that floor. Untick to render everything at the raw global
  // delay clock: the slider then maps 1:1 to what you see (at the cost of the
  // hold-and-jump stutter adaptive exists to hide).
  const [adaptiveSmooth, setAdaptiveSmooth] = useState<boolean>(() => {
    try { return localStorage.getItem("ar.netAdaptive") !== "0"; } catch { return true; }
  });
  const toggleAdaptive = (on: boolean) => {
    setAdaptiveSmooth(on);
    try { localStorage.setItem("ar.netAdaptive", on ? "1" : "0"); } catch { /* no storage */ }
  };
  // Which live game views are mounted (see VIEWS): off = canvas unmounted (its rAF
  // loop stops), a same-size placeholder holds the grid cell.
  const [views, setViews] = useState<Record<ViewKey, boolean>>(readViews);
  const toggleView = (k: ViewKey) => setViews((v) => {
    const next = { ...v, [k]: !v[k] };
    try { localStorage.setItem(VIEWS_KEY, JSON.stringify(next)); } catch { /* no storage */ }
    return next;
  });
  // Hovering an event with a coordinate drops a green marker at that spot on every canvas.
  const [hoverPt, setHoverPt] = useState<HoverPt | null>(null);
  // Hovering a worm_segments row (or a single segment) in the WireLog draws those segments on every
  // canvas: solid yellow lines with green/red endpoints + dashed magenta gaps between them.
  const [hoverSegs, setHoverSegs] = useState<any[] | null>(null);
  // Power-up testing: which powerUpDefinitions keys may spawn. Default all on. The
  // harness enables ALL effect types (disabledEffectTypes: []) and boosts the spawn
  // rate so a chosen power-up (e.g. twin) appears fast. Changing this restarts the
  // match (below), since the spawn set is fixed at match start.
  const [enabledPu, setEnabledPu] = useState<string[]>(readPowerupsParam);
  useEffect(() => { writePowerupsParam(enabledPu); }, [enabledPu]);
  const autoRef = useRef({ on: false });
  autoRef.current.on = autoC1;

  // Panel refs (order: c1 truth, server truth, c2 truth, c1 smooth, c2 smooth).
  const boxRefs = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)];

  // Recording / playback.
  const [mode, setMode] = useState<"live" | "playback">("live");
  // TEMP DEBUG (switcharoonie bring-up): disable all crashes. Live-toggleable button below; seeded
  // from ?nocrash=1 and written back to the URL so a reload / match restart keeps it.
  const [noCrash, setNoCrash] = useState(() => new URLSearchParams(window.location.search).get("nocrash") === "1");
  // Apply the flag to core (single module-level flag shared by the in-browser C1/SV/C2) whenever it
  // changes, and clear it on unmount so leaving the harness never leaves crashes disabled. Also mirror
  // it into the URL. Kept OUT of the match-setup effect so a match restart doesn't reset the toggle.
  useEffect(() => {
    cf.setDebugNoCrash(noCrash);
    const q = new URLSearchParams(window.location.search);
    if (noCrash) q.set("nocrash", "1"); else q.delete("nocrash");
    const qs = q.toString();
    window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
    return () => cf.setDebugNoCrash(false);
  }, [noCrash]);
  const [recording, setRecording] = useState(false);
  const framesRef = useRef<Frame[]>([]);
  const [frameCount, setFrameCount] = useState(0);
  const [playIdx, setPlayIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const recRef = useRef({ on: false });
  recRef.current.on = recording;
  // The InfoPanel writes a fresh-snapshot getter here so the recorder can capture the
  // whole right rail per frame (it then rewinds with the scrubber in playback).
  const snapRef = useRef<(() => InfoSnap) | null>(null);

  useEffect(() => {
    setReady(null); // tear down the previous match's view while the new one spins up
    const bus = createHarnessBus(readNetParams());
    condRef.current = bus.conditions;
    const server = createHarnessServer(bus, {
      expectedClients: 2,
      mapSize: MAP_SIZE,
      disabledPowerUps: ALL_POWERUP_KEYS.filter((k) => enabledPu.indexOf(k) === -1),
      disabledEffectTypes: [], // harness tests them all, incl. the online-disabled ones
      powerUpSpawnChance: HARNESS_SPAWN_CHANCE,
      debugStartEffects: readStartFx(), // ?startfx=size:2:30 -> everyone fat for 30s
      // ?respawn=60 -> a Respawn round of 60 s (core respawn.js)
      respawn: (() => { const v = Number(new URLSearchParams(window.location.search).get("respawn")); return isFinite(v) && v > 0 ? { roundTime: v } : null; })(),
      debugForceSwitchAt: (() => { const v = Number(new URLSearchParams(window.location.search).get("forceswitch")); return isFinite(v) && v > 0 ? v : null; })(), // ?forceswitch=4 -> switcharoonie at t=4s
      // Host-simulated bots. Default 0 for the slim two-worm setup (you vs one
      // opponent); ?serverbots=1 (or more) adds pure server worms so the match
      // covers that ownership flavour too.
      serverBots: (() => { const q = new URLSearchParams(window.location.search); if (!q.has("serverbots")) return 0; const v = Number(q.get("serverbots")); return isFinite(v) && v >= 0 ? Math.min(3, Math.floor(v)) : 0; })(),

      // maxScore 2 (not 1) so winning the round does NOT end the match: the victory lap then
      // plays and the winner stays steerable (with maxScore 1 the match was over the instant a
      // worm died, freezing before any lap). We freeze the sim at the END of that lap ourselves
      // (auto-record effect below), so we still stop after one round + its lap - no round 2.
      maxScore: 2,
    });
    const keys = { left: false, right: false };
    const c1Ai = createAiDriver(() => server.getServerGameState());
    // Default: the slim setup - you (C1, keyboard) vs ONE opponent (C2, a second
    // real client, bot-steered). For full player-type coverage add back C1's
    // bot-steered second local player with ?locals=2 (or 3) and a pure server worm
    // with ?serverbots=1, so every ownership flavour is in one match.
    const c1Locals: { name: string; colorId: string; drive?: (gs: any, t: number, myId: string | null) => number }[] = [
      { name: "You", colorId: "blue", drive: (gs, t, myId) => (autoRef.current.on ? c1Ai(gs, t, myId) : (keys.right ? 1 : 0) - (keys.left ? 1 : 0)) },
    ];
    const localsWanted = (() => { const v = Number(new URLSearchParams(window.location.search).get("locals")); return isFinite(v) && v >= 1 ? Math.min(3, Math.floor(v)) : 1; })();
    for (let i = 2; i <= localsWanted; i++) {
      c1Locals.push({ name: "You " + i, colorId: i === 2 ? "green" : "orange", drive: createAiDriver(() => server.getServerGameState()) });
    }
    const c1 = createHarnessClient({ bus, id: "c1", localPlayers: c1Locals, renderDelayMs: readDelayMs() });
    const c2 = createHarnessClient({ bus, id: "c2", name: "C2", colorId: "pink", drive: createAiDriver(() => server.getServerGameState()), renderDelayMs: readDelayMs() });
    const isLeft = (k: string) => k === "ArrowLeft" || k === "a" || k === "A";
    const isRight = (k: string) => k === "ArrowRight" || k === "d" || k === "D";
    const down = (e: KeyboardEvent) => { if (isLeft(e.key)) keys.left = true; else if (isRight(e.key)) keys.right = true; };
    const up = (e: KeyboardEvent) => { if (isLeft(e.key)) keys.left = false; else if (isRight(e.key)) keys.right = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    const poll = setInterval(() => {
      const gs = server.getServerGameState();
      if (gs && c1.getGameState() && c2.getGameState()) {
        const players = server.seats().map((s) => ({ id: s.playerId, name: s.name, colorId: s.colorId, type: "human" as const, left: null, right: null }));
        clearInterval(poll);
        (window as any).__harness = { bus, server, c1, c2 }; // debug inspection hook
        setReady({ bus, server, c1, c2, players });
      }
    }, 50);
    return () => {
      clearInterval(poll);
      window.removeEventListener("keydown", down); window.removeEventListener("keyup", up);
      c1.dispose(); c2.dispose(); server.dispose(); bus.dispose();
    };
    // Restart the match when the power-up selection changes (spawn set is fixed at start).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledPu]);

  const r = ready;

  // Download the full wire log (every message both directions) as JSON for
  // offline analysis. Optionally clears it after, to start a fresh capture.
  function dumpWireLog(clear: boolean) {
    if (!r) return;
    const log = r.bus.getLog();
    const payload = {
      meta: {
        exportedAt: new Date().toISOString(),
        conditions: { ...r.bus.conditions },
        count: log.length,
        players: r.players.map((p) => ({ id: p.id, name: p.name, colorId: p.colorId })),
        legend: "times are ms since harness start; dir c2s=client->server, s2c=server->client; gameTime is the sim clock; dropped=true means the loss model never delivered it",
      },
      log,
    };
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `netlog-${log.length}msgs.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    if (clear) r.bus.clearLog();
  }

  // Capture loop while recording. Grabs all five panels + the diagnostics in one
  // pass, so every stored frame is a single synchronized earth-instant.
  useEffect(() => {
    if (!recording || !r) return;
    const id = setInterval(() => {
      if (!recRef.current.on) return;
      const images = boxRefs.map((ref) => capturePanel(ref.current));
      const info = snapRef.current ? snapRef.current() : null;
      framesRef.current.push({ images, info, t: framesRef.current.length * CAP_MS, logLen: r.bus.getLog ? r.bus.getLog().length : 0 });
      // NB: intentionally NO setState per captured frame. A DebugNetPage re-render makes
      // every GameCanvas clear() itself (it redraws on the next rAF), and a capture landing
      // in that gap grabs a black frame. The canvases render via their own rAF loop, so we
      // just push frames and leave React alone; the count updates when recording stops.
      if (framesRef.current.length >= CAP_MAX) { setRecording(false); setFrameCount(framesRef.current.length); }
    }, CAP_MS);
    return () => clearInterval(id);
  }, [recording, r]);

  // Auto-record: start capturing when the match is live; play round 1 AND its victory lap
  // (the winner stays steerable), then freeze the sim just before round 2 - so we stop after
  // one round + lap with no fresh round-2 data. We STAY on the live (now frozen) view rather
  // than jumping to playback; the "▶ Replay" button scrubs the recording on demand.
  useEffect(() => {
    if (!r) return;
    let done = false;
    let lapTimer: any = null;
    startRecording();
    const id = setInterval(() => {
      if (done || lapTimer) return;
      const log = r.bus.getLog ? r.bus.getLog() : [];
      // Round over -> the server sent game_countdown and entered the victory lap. Let the lap
      // run its full duration (winner steerable), then freeze the runner just before round 2.
      const countdown = log.find((m: any) => m.type === "game_countdown");
      if (!countdown) return;
      const lapMs = (countdown.msg && countdown.msg.duration) || 5000;
      lapTimer = setTimeout(() => {
        done = true;
        clearInterval(id);
        setRecording(false);
        setFrameCount(framesRef.current.length);
        const runner = r.server.getRunner && r.server.getRunner();
        if (runner && runner.stop) runner.stop(); // freeze at the end of the lap (no round 2)
      }, Math.max(0, lapMs - 250));
    }, 120);
    return () => { clearInterval(id); if (lapTimer) clearTimeout(lapTimer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r]);

  // Playback advance.
  useEffect(() => {
    if (mode !== "playback" || !playing) return;
    let raf = 0; let last = 0;
    const tick = (now: number) => {
      if (last) {
        // advance by real elapsed / CAP_MS frames
        const adv = (now - last) / CAP_MS;
        setPlayIdx((i) => {
          const ni = i + adv;
          if (ni >= framesRef.current.length - 1) { setPlaying(false); return framesRef.current.length - 1; }
          return ni;
        });
      }
      last = now;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [mode, playing]);

  function startRecording() { framesRef.current = []; setFrameCount(0); setRecording(true); setMode("live"); }
  function stopRecording() { setRecording(false); if (framesRef.current.length) { setPlayIdx(0); setPlaying(false); setMode("playback"); } }
  function backToLive() { setMode("live"); setPlaying(false); }
  function step(d: number) { setPlaying(false); setPlayIdx((i) => Math.max(0, Math.min(framesRef.current.length - 1, Math.round(i) + d))); }

  const cond = condRef.current;
  const ghost1 = r ? { getSegment: r.c1.remote.getServerLastSegment, getTime: r.c1.getServerTime } : undefined;
  const ghost2 = r ? { getSegment: r.c2.remote.getServerLastSegment, getTime: r.c2.getServerTime } : undefined;
  const mapW = (r && r.server.getServerGameState()?.map?.width) || 400;
  const frames = framesRef.current;
  const curFrame = mode === "playback" && frames.length ? frames[Math.round(Math.max(0, Math.min(frames.length - 1, playIdx)))] : null;

  return (
    <div style={{ minHeight: "100vh", background: "#05070d", color: "#eaf0ff", padding: 20, fontFamily: "'Rajdhani',sans-serif" }}>
      <h1 style={{ fontFamily: "'Orbitron',sans-serif", fontWeight: 900, fontSize: 20, letterSpacing: 2, margin: "0 0 4px" }}>NETCODE HARNESS{mode === "playback" ? " · REPLAY (wall-clock)" : ""}</h1>
      <div style={{ color: "#8995b3", fontSize: 13, marginBottom: 14 }}>
        {mode === "playback" ? "Faithful replay: exactly what each screen showed at the same instant. Step / scrub." : "Client 1 = you (arrow keys / A-D). Client 2 = one opponent (a second client, bot-steered). All local, over a simulated network. ?locals=2 adds a bot-steered local player on C1; ?serverbots=1 adds a server-simulated worm."}
      </div>
      {noCrash && (
        <div style={{ background: "#3a1220", border: "1px solid #ff5c7a", color: "#ff9db3", fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 4, marginBottom: 14, display: "inline-block" }}>
          ⚠ CRASHES DISABLED · worms can't die · temp switcharoonie bring-up
        </div>
      )}

      {mode === "live" && (
        <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          {cond && (["ping", "jitter", "loss"] as const).map((k) => (
            <label key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
              <span style={{ width: 46, color: "#cbd3e6", textTransform: "uppercase", fontSize: 11, letterSpacing: 1 }}>{k}</span>
              <input type="range" min={0} max={k === "loss" ? 0.4 : 500} step={k === "loss" ? 0.01 : 10} defaultValue={cond[k]} onChange={(e) => { const v = Number(e.target.value); cond[k] = v; writeNetParam(k, v); force((n) => n + 1); }} />
              <span style={{ width: 52, color: "#7CFC66", fontVariantNumeric: "tabular-nums" }}>{k === "loss" ? Math.round(cond.loss * 100) + "%" : cond[k] + "ms"}</span>
            </label>
          ))}
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <span style={{ width: 46, color: "#cbd3e6", textTransform: "uppercase", fontSize: 11, letterSpacing: 1 }}>delay</span>
            <input
              type="range" min={0} max={400} step={10} value={delayMs}
              onChange={(e) => {
                const v = Number(e.target.value);
                setDelayMs(v);
                try { localStorage.setItem(DELAY_KEY, String(v)); } catch { /* no storage */ }
                if (r) { r.c1.remote.setRenderDelay(v / 1000); r.c2.remote.setRenderDelay(v / 1000); }
              }}
            />
            <span style={{ width: 52, color: "#7CFC66", fontVariantNumeric: "tabular-nums" }}>{delayMs}ms</span>
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#cbd3e6" }} title="Per-opponent adaptive clocks (production behavior). Opponents render no fresher than their data floor (one-way latency + batching + 80ms margin), so the DELAY slider only bites above that. Untick for the raw global delay clock: the slider maps 1:1 to what you see.">
            <input type="checkbox" checked={adaptiveSmooth} onChange={(e) => toggleAdaptive(e.target.checked)} /> adaptive
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#cbd3e6" }}>
            <input type="checkbox" checked={autoC1} onChange={(e) => setAutoC1(e.target.checked)} /> Auto-drive Client 1 (bot)
          </label>
          <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#cbd3e6" }}>
            <span style={{ textTransform: "uppercase", fontSize: 11, letterSpacing: 1, color: "#6b7590" }}>views</span>
            {VIEWS.map((v) => (
              <label key={v.key} style={{ display: "flex", alignItems: "center", gap: 4, color: v.accent }} title={`${v.title} · ${v.subtitle}`}>
                <input type="checkbox" checked={views[v.key]} onChange={() => toggleView(v.key)} />
                {v.title.replace("Client ", "C").replace("Server", "SV")}·{v.subtitle![0]}
              </label>
            ))}
          </span>
          <button onClick={() => setNoCrash((v) => !v)} style={btn(noCrash ? "#ff5c7a" : undefined)} title="Disable ALL crashes (core + client) so no worm can die - switcharoonie bring-up">
            {noCrash ? "☠ crashes OFF" : "☠ crashes on"}
          </button>
          {!recording ? (
            <button onClick={startRecording} disabled={!r} style={btn("#ff5c7a")}>● Record</button>
          ) : (
            <button onClick={stopRecording} style={btn("#ff9f45")}>■ Stop &amp; replay · recording…</button>
          )}
          {frames.length > 0 && !recording && <button onClick={() => { setPlayIdx(0); setPlaying(false); setMode("playback"); }} style={btn("#7CFC66")}>▶ Replay ({frames.length})</button>}
          {r && <button onClick={() => dumpWireLog(false)} style={btn("#28c4d4")} title="Download every message sent this session as JSON">⤓ Dump wire log</button>}
          {r && <button onClick={() => { r.bus.clearLog(); force((n) => n + 1); }} style={btn()} title="Start a fresh capture">clear log</button>}
        </div>
      )}

      {mode === "live" && (
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <span style={{ color: "#cbd3e6", textTransform: "uppercase", fontSize: 11, letterSpacing: 1, marginRight: 4 }}>Power-ups</span>
          <button onClick={() => setEnabledPu(ALL_POWERUP_KEYS)} style={btn()} title="Spawn all power-ups">all</button>
          <button onClick={() => setEnabledPu([])} style={btn()} title="Spawn none">none</button>
          {ORDERED_PU_KEYS.map((k) => {
            const on = enabledPu.indexOf(k) !== -1;
            const meta = PU_META[k];
            const label = meta?.name || (powerUpDefinitions as any)[k]?.name || k;
            return (
              <button
                key={k}
                onClick={(e) => {
                  // Shift-click = solo this power-up (fastest way to test just one).
                  if (e.shiftKey) return setEnabledPu([k]);
                  setEnabledPu((cur) => (cur.indexOf(k) !== -1 ? cur.filter((x) => x !== k) : cur.concat(k)));
                }}
                title="Click to toggle · Shift-click to test only this one"
                style={{
                  ...btn(on ? (meta ? POWERUP_BADGE[meta.color] : "#7CFC66") : undefined),
                  opacity: on ? 1 : 0.45,
                  padding: "3px 8px 3px 4px",
                  fontSize: 12,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                {meta ? (
                  <span style={{ width: 16, height: 16, flex: "0 0 auto", borderRadius: "50%", background: POWERUP_BADGE[meta.color], display: "inline-flex", alignItems: "center", justifyContent: "center", filter: on ? "none" : "grayscale(1)" }}>
                    <img src={`/svg/powerup/${meta.icon}.svg`} alt="" style={{ width: "100%", height: "100%", display: "block" }} />
                  </span>
                ) : null}
                {label}
              </button>
            );
          })}
        </div>
      )}

      {mode === "playback" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, maxWidth: CANVAS * 3 + 36 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={backToLive} style={btn("#ff9f45")}>◀ Back to live</button>
            <button onClick={() => step(-1)} style={btn()}>⟨ frame</button>
            <button onClick={() => setPlaying((p) => !p)} style={btn("#7CFC66")}>{playing ? "❚❚ pause" : "▶ play"}</button>
            <button onClick={() => step(1)} style={btn()}>frame ⟩</button>
            <span style={{ color: "#7CFC66", fontVariantNumeric: "tabular-nums", fontSize: 14 }}>frame {Math.round(playIdx) + 1} / {frames.length} &nbsp; t = {(Math.round(playIdx) * CAP_MS / 1000).toFixed(2)}s</span>
          </div>
          <input type="range" min={0} max={Math.max(0, frames.length - 1)} step={1} value={Math.round(playIdx)} onChange={(e) => { setPlaying(false); setPlayIdx(Number(e.target.value)); }} style={{ width: "100%" }} />
        </div>
      )}

      {!r ? (
        <div style={{ color: "#8995b3" }}>Starting match…</div>
      ) : mode === "playback" && curFrame ? (
        <>
          <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
            <div style={{ display: "grid", gridTemplateColumns: `${CANVAS}px ${CANVAS}px ${CANVAS}px`, gap: 18, flex: "0 0 auto" }}>
              <ImgPanel title="Client 1" subtitle="truth" accent="#4aa3ff" src={curFrame.images[0]} highlight={hoverPt} segHi={hoverSegs} owner="c1" />
              <ImgPanel title="Server" subtitle="truth" accent="#7CFC66" src={curFrame.images[1]} highlight={hoverPt} segHi={hoverSegs} owner="sv" />
              <ImgPanel title="Client 2" subtitle="truth" accent="#ff5c7a" src={curFrame.images[2]} highlight={hoverPt} segHi={hoverSegs} owner="c2" />
              <ImgPanel title="Client 1" subtitle="smooth" accent="#4aa3ff" src={curFrame.images[3]} highlight={hoverPt} segHi={hoverSegs} owner="c1" />
              <WireLog getLog={r.bus.getLog} untilIndex={curFrame.logLen} onHoverSegs={setHoverSegs} />
              <ImgPanel title="Client 2" subtitle="smooth" accent="#ff5c7a" src={curFrame.images[4]} highlight={hoverPt} segHi={hoverSegs} owner="c2" />
            </div>
            <InfoView snap={curFrame.info} onHover={setHoverPt} />
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
            <div style={{ display: "grid", gridTemplateColumns: `${CANVAS}px ${CANVAS}px ${CANVAS}px`, gap: 18, flex: "0 0 auto" }}>
              {views.c1t ? <Panel title="Client 1" subtitle="truth" accent="#4aa3ff" boxRef={boxRefs[0]} owner="c1" getTime={r.c1.ownNow} mapWidth={mapW} highlight={hoverPt} segHi={hoverSegs}><View gameState={r.c1.getGameState()} players={r.players} renderTime={r.c1.getServerTime} localPlayerIds={r.c1.localIds()} predictors={r.c1.predictors()} getTargetTime={r.c1.ownNow} serverGhost={ghost1} /></Panel> : <OffPanel title="Client 1" subtitle="truth" accent="#4aa3ff" />}
              {views.svt ? <Panel title="Server" subtitle="truth" accent="#7CFC66" boxRef={boxRefs[1]} owner="sv" getTime={() => r.server.getServerGameState()?.gameTime ?? 0} mapWidth={mapW} highlight={hoverPt} segHi={hoverSegs}><View gameState={r.server.getServerGameState()} players={r.players} /></Panel> : <OffPanel title="Server" subtitle="truth" accent="#7CFC66" />}
              {views.c2t ? <Panel title="Client 2" subtitle="truth" accent="#ff5c7a" boxRef={boxRefs[2]} owner="c2" getTime={r.c2.ownNow} mapWidth={mapW} highlight={hoverPt} segHi={hoverSegs}><View gameState={r.c2.getGameState()} players={r.players} renderTime={r.c2.getServerTime} localPlayerIds={r.c2.localIds()} predictors={r.c2.predictors()} getTargetTime={r.c2.ownNow} serverGhost={ghost2} /></Panel> : <OffPanel title="Client 2" subtitle="truth" accent="#ff5c7a" />}
              {views.c1s ? <Panel title="Client 1" subtitle="smooth" accent="#4aa3ff" boxRef={boxRefs[3]} owner="c1" getTime={r.c1.getRenderTime} mapWidth={mapW} highlight={hoverPt} segHi={hoverSegs}><View gameState={r.c1.getGameState()} players={r.players} renderTime={r.c1.getRenderTime} localPlayerIds={r.c1.localIds()} predictors={r.c1.predictors()} getTargetTime={r.c1.ownNow} adaptive={adaptiveSmooth} powerUpTruthTime={r.c1.getServerTime} /></Panel> : <OffPanel title="Client 1" subtitle="smooth" accent="#4aa3ff" />}
              <WireLog getLog={r.bus.getLog} onHoverSegs={setHoverSegs} />
              {views.c2s ? <Panel title="Client 2" subtitle="smooth" accent="#ff5c7a" boxRef={boxRefs[4]} owner="c2" getTime={r.c2.getRenderTime} mapWidth={mapW} highlight={hoverPt} segHi={hoverSegs}><View gameState={r.c2.getGameState()} players={r.players} renderTime={r.c2.getRenderTime} localPlayerIds={r.c2.localIds()} predictors={r.c2.predictors()} getTargetTime={r.c2.ownNow} adaptive={adaptiveSmooth} powerUpTruthTime={r.c2.getServerTime} /></Panel> : <OffPanel title="Client 2" subtitle="smooth" accent="#ff5c7a" />}
            </div>
            <InfoPanel r={r} onHover={setHoverPt} snapRef={snapRef} />
          </div>
        </>
      )}
    </div>
  );
}

function ImgPanel({ title, subtitle, accent, src, mapWidth = 400, highlight, segHi, owner }: { title: string; subtitle?: string; accent?: string; src: string; mapWidth?: number; highlight?: HoverPt | null; segHi?: any[] | null; owner?: HoverOwner }) {
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const g2p = (v: number) => v * (CANVAS / mapWidth);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 0.5, color: accent || "#cbd3e6" }}>
        {title}{subtitle ? <span style={{ color: "#6b7590", fontWeight: 600 }}> · {subtitle}</span> : null}
      </div>
      <div
        onMouseMove={(e) => { const b = e.currentTarget.getBoundingClientRect(); setHover({ x: (e.clientX - b.left) * (mapWidth / CANVAS), y: (e.clientY - b.top) * (mapWidth / CANVAS) }); }}
        onMouseLeave={() => setHover(null)}
        style={{ width: CANVAS, height: CANVAS, background: "#070a12", border: `1px solid ${accent ? accent + "55" : "rgba(255,255,255,0.1)"}`, borderRadius: 6, overflow: "hidden", position: "relative" }}
      >
        {src ? <img src={src} width={CANVAS} height={CANVAS} alt="" style={{ display: "block" }} /> : null}
        {hover ? <div style={{ position: "absolute", top: 4, left: 6, padding: "1px 5px", borderRadius: 4, background: "rgba(0,0,0,0.65)", color: "#c4ccdd", fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11, pointerEvents: "none", zIndex: 1000 }}>{hover.x | 0}, {hover.y | 0}</div> : null}
        {highlight && (!highlight.owner || highlight.owner === owner) ? <CrossHair px={g2p(highlight.x)} py={g2p(highlight.y)} /> : null}
        {segHi && segHi.length ? <SegOverlay segs={segHi} g2p={g2p} /> : null}
      </div>
    </div>
  );
}

function LiveDiag({ get }: { get: () => DiagRow[] }) {
  const [rows, setRows] = useState<DiagRow[]>([]);
  useEffect(() => { const t = setInterval(() => setRows(get()), 200); return () => clearInterval(t); }, []);
  return (
    <>
      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, color: "#cbd3e6", fontSize: 14, marginBottom: 6 }}>Diagnostics <span style={{ color: "#6b7590", fontWeight: 600 }}>(lag = how far each copy trails the server, game-seconds · content = opponent copy vs server)</span></div>
      <DiagTable rows={rows} />
    </>
  );
}

// ---- Live info panel (right rail) -------------------------------------------
// Everything that made this session's netcode bugs slow to diagnose, surfaced live:
// per-worm state as each copy (C1/SV/C2) sees it (effects + timeLeft caught the wall-hack
// effect-timing race; head pos + ghost count caught the wrap), an event feed of what fires
// back and forth (deaths, pickups, effects, crash reports, wraps), and a death-divergence
// readout (where the client crashed vs where the server killed it = the retraction cause).
type FeedKind = "death" | "pickup" | "effect" | "effect-end" | "crash" | "wrap" | "lap" | "teleport";
type FeedSrc = "c1" | "sv" | "c2"; // whose event this is (blue dot / none / red dot)
interface FeedItem { id: number; t: number; kind: FeedKind; src: FeedSrc; text: string; x?: number; y?: number }
const FEED_COLOR: Record<FeedKind, string> = {
  death: "#ff4d5e", pickup: "#28c4d4", effect: "#7CFC66", "effect-end": "#6b7590", crash: "#ff9f45", wrap: "#c084fc", lap: "#f5c542", teleport: "#ff2fd0",
};
const SRC_COLOR: Record<FeedSrc, string | null> = { c1: "#4aa3ff", sv: null, c2: "#ff5c7a" };

function mapBounds(gs: any): { x0: number; y0: number; x1: number; y1: number } | null {
  const s = gs && gs.map && gs.map.shape; if (!s) return null;
  const x0 = s.x ?? 0, y0 = s.y ?? 0;
  const x1 = s.maxX ?? (x0 + (s.width ?? 0)), y1 = s.maxY ?? (y0 + (s.height ?? 0));
  return { x0, y0, x1, y1 };
}
function headOfWorm(gs: any, _playerId: string, wormId: string): { x: number; y: number; t: number } | null {
  // Look the track up by the worm's stable trackId, not playerId_wormId (a switcharoonie changes
  // the owner but not the key).
  const w = gs && gs.worms && gs.worms.find((x: any) => x.id === wormId);
  const segs = w && gs.wormPathSegments && gs.wormPathSegments[w.trackId];
  if (!segs || !segs.length) return null;
  const s = segs[segs.length - 1];
  return { x: s.endX, y: s.endY, t: (s.startTime || 0) + (s.duration || 0) };
}
// Some power-ups share one effectType and differ only by strength (fat/slim -> "size",
// speed/slow -> "speed", quick/slow turn -> "turningSpeed"). Show the actual power-up so the
// debugger reads "fat"/"thin" instead of an ambiguous "size".
function effectLabel(e: any): string {
  const s = e.strength;
  if (e.type === "size") return s != null ? (s >= 1 ? "fat" : "thin") : "size";
  if (e.type === "speed") return s != null ? (s >= 1 ? "speed" : "slow") : "speed";
  if (e.type === "turningSpeed") return s != null ? (s >= 1 ? "quick-turn" : "slow-turn") : "turningSpeed";
  return e.type;
}
function effectsOfWorm(gs: any, wormId: string): { type: string; t: number; predicted: boolean }[] {
  return (gs && gs.effects ? gs.effects : []).filter((e: any) => e.wormId === wormId)
    .map((e: any) => ({ type: effectLabel(e), t: e.timeLeft ?? e.duration ?? 0, predicted: !!e._predicted }));
}
// The wall-hack MIRROR (ghost) tracks of a worm: each mirror's leading-tip position, and
// "mirroring" = mirror heads are being drawn for it right now. For a worm THIS client predicts
// (its own) we read the predictor's AUTHORITATIVE mirrorHeads - the exact thing the renderer
// draws. For a worm it doesn't predict (an opponent / the server view) we fall back to the
// positional check (head straddling an edge).
function ghostTracksOfWorm(gs: any, playerId: string, wormId: string, predictor: any): GhostRow[] {
  const tracks = (gs && gs.wormPathSegments) || {};
  // Own worm: read the predictor's authoritative mirrorHeads (exactly what the predicted renderer
  // draws). Opponent / server: read the STREAMED ghost segments - a mirror track is "mirroring"
  // iff its tip is still being extended at ~now (the same signal the head renderer uses to draw
  // its head), so once the owner stops straddling and `now` overruns the tip it flips to idle.
  const ownActive = predictor && predictor.mirrorHeads ? predictor.mirrorHeads(wormId).length > 0 : null;
  const now = gs ? gs.gameTime : 0;
  const w = gs && gs.worms && gs.worms.find((x: any) => x.id === wormId);
  const pre = (w ? w.trackId : playerId + "_" + wormId) + "#"; // stable trackId prefix (switch-safe)
  return Object.keys(tracks).filter((k) => k.indexOf(pre) === 0).map((k) => {
    const segs = tracks[k];
    const last = segs[segs.length - 1];
    const active = ownActive !== null ? ownActive : last.endTime >= now - 0.08;
    return { id: k.slice(k.indexOf("#")), x: last.endX, y: last.endY, active };
  });
}

// ---- Snapshot of the whole right-rail, so it can be recorded per frame and REWOUND in
// replay (scrubbing shows the diagnostics/worms/deaths/events as they were then). ----
interface GhostRow { id: string; x: number; y: number; active: boolean }
interface OwnerWorm { id: string; color: string; x: number | null; y: number | null; effects: { type: string; t: number; predicted: boolean }[]; ghosts: GhostRow[] }
interface OwnerList { label: string; tag: HoverOwner; accent: string; list: OwnerWorm[]; switch: "switching" | "frozen" | null }
interface DRow { key: string; svT: number; svX: number; svY: number; clT: number | null; clX: number | null; clY: number | null }
// Segment inspector (#1) + continuity flag (#2): each worm's LATEST path segment on each view.
// `len` = this segment's own length; `gap` = distance from the previous segment's end to this
// one's start (should be ~0; a big gap = a discontinuity/jump WITHIN the track).
interface SegView { view: HoverOwner; speed: number; len: number; dur: number; gap: number; type: string }
interface SegRow { id: string; color: string; views: (SegView | null)[] } // [C1, SV, C2]
interface InfoSnap { diag: DiagRow[]; worms: OwnerList[]; deaths: DRow[]; events: FeedItem[]; net: { ping: number; jitter: number; loss: number; msgs: number }; segs: SegRow[] }
// The latest segment of a worm on a given view (keyed by the worm's stable trackId), + the gap to
// the previous segment's end (switch-safe: ownership can change but trackId doesn't).
function latestSeg(gs: any, wormId: string, view: HoverOwner): SegView | null {
  const w = gs && gs.worms && gs.worms.find((x: any) => x.id === wormId);
  const segs = w && gs.wormPathSegments && gs.wormPathSegments[w.trackId];
  if (!segs || !segs.length) return null;
  const last = segs[segs.length - 1];
  const prev = segs.length >= 2 ? segs[segs.length - 2] : null;
  const len = Math.hypot((last.endX || 0) - (last.startX || 0), (last.endY || 0) - (last.startY || 0));
  const gap = prev ? Math.hypot((last.startX || 0) - (prev.endX || 0), (last.startY || 0) - (prev.endY || 0)) : 0;
  return { view, speed: last.speed || 0, len, dur: last.duration || 0, gap, type: last.type || "?" };
}

const IBOX: React.CSSProperties = { background: "#0e1420", border: "1px solid #1e2636", borderRadius: 8, padding: "10px 12px", marginBottom: 12 };
const IHDR: React.CSSProperties = { fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, color: "#cbd3e6", fontSize: 13, marginBottom: 8, letterSpacing: 0.3 };
const ISUB: React.CSSProperties = { color: "#6b7590", fontSize: 11, fontWeight: 600 };
const IMONO: React.CSSProperties = { fontFamily: "ui-monospace,Menlo,monospace", fontSize: 11.5, fontVariantNumeric: "tabular-nums" };

// Live view of EVERY wire message (the same log "Dump wire log" saves), so you can inspect the
// exact switch_freeze / worm_segments / worm_crashed / ... and its payload without the network tab.
// Filter by type (noisy ones default-off); click a row to expand its JSON. Self-polls.
const WIRE_NOISY = ["worm_segments", "rtt_ping", "rtt_ping_ack", "game_update", "player_steering"];
// A wire message's worm segments, if it is a worm_segments message (else null).
function segsOf(e: any): any[] | null { return e && e.msg && Array.isArray(e.msg.segments) ? e.msg.segments : null; }
function WireLog({ getLog, untilIndex, onHoverSegs }: { getLog?: () => any[]; untilIndex?: number; onHoverSegs?: (segs: any[] | null) => void }) {
  const [, force] = useState(0);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set(WIRE_NOISY));
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  // Live: self-poll so new messages appear. Replay: the log is frozen and we slice to the
  // frame, so no polling needed (the parent re-renders as the user scrubs).
  useEffect(() => { if (untilIndex != null) return; const id = setInterval(() => force((n) => n + 1), 250); return () => clearInterval(id); }, [untilIndex]);
  // Clear any lingering canvas highlight when this WireLog unmounts (e.g. live <-> replay swap).
  useEffect(() => () => onHoverSegs?.(null), [onHoverSegs]);
  const full = getLog ? getLog() : [];
  // In replay, only messages that had been sent by the current frame (logLen captured per frame).
  const log = untilIndex != null ? full.slice(0, untilIndex) : full;
  const types = Array.from(new Set(log.map((e: any) => e.type))).sort();
  const rows = log.filter((e: any) => !hidden.has(e.type)).slice(-400).reverse();
  const toggle = (t: string) => setHidden((h) => { const n = new Set(h); if (n.has(t)) n.delete(t); else n.add(t); return n; });
  const toggleOpen = (seq: number) => setOpen((o) => { const n = new Set(o); if (n.has(seq)) n.delete(seq); else n.add(seq); return n; });
  return (
    <div style={{ ...IBOX, width: CANVAS, marginBottom: 0, display: "flex", flexDirection: "column", maxHeight: CANVAS + 22, boxSizing: "border-box" }}>
      <div style={IHDR}>Wire log <span style={ISUB}>· {untilIndex != null ? `replay: ${log.length} msgs by this frame` : "every message"} · hover a worm_segments row to draw it · click to expand</span></div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginBottom: 6 }}>
        {types.map((t) => (
          <span key={t} onClick={() => toggle(t)} title={hidden.has(t) ? "hidden - click to show" : "shown - click to hide"}
            style={{ cursor: "pointer", padding: "1px 5px", borderRadius: 3, fontSize: 9.5, fontWeight: 700, border: "1px solid #24304a", color: hidden.has(t) ? "#4b556e" : "#dbe2f2", background: hidden.has(t) ? "transparent" : "#233150" }}>{t}</span>
        ))}
      </div>
      <div style={{ overflowY: "auto", flex: 1, ...IMONO, fontSize: 10.5 }}>
        {rows.map((e: any) => {
          const c2s = e.dir === "c2s";
          const segs = segsOf(e);
          return (
            <div key={e.seq} style={{ borderBottom: "1px solid #141a28" }}
              onMouseEnter={segs ? () => onHoverSegs?.(segs) : undefined}
              onMouseLeave={segs ? () => onHoverSegs?.(null) : undefined}>
              <div onClick={() => toggleOpen(e.seq)} style={{ cursor: "pointer", padding: "1px 2px", display: "flex", gap: 6, whiteSpace: "nowrap", background: segs ? "rgba(255,223,58,0.06)" : undefined }}>
                <span style={{ color: "#6b7590" }}>{(e.sentAt / 1000).toFixed(2)}</span>
                <span style={{ color: c2s ? "#4aa3ff" : "#7CFC66" }}>{c2s ? "→" : "←"}{e.from}</span>
                <span style={{ color: "#cbd3e6", fontWeight: 700 }}>{e.type}</span>
                {segs ? <span style={{ color: "#ffdf3a" }}>×{segs.length}{e.msg.playerId ? " " + e.msg.playerId : ""}</span> : null}
              </div>
              {open.has(e.seq) ? (
                segs ? (
                  <div style={{ padding: "2px 4px", background: "#0a0e18" }}>
                    {segs.map((s: any, i: number) => {
                      const prev = i > 0 ? segs[i - 1] : null;
                      const gap = prev && prev.endX != null ? Math.hypot((s.startX || 0) - (prev.endX || 0), (s.startY || 0) - (prev.endY || 0)) : 0;
                      return (
                        <div key={i} onMouseEnter={() => onHoverSegs?.([s])} onMouseLeave={() => onHoverSegs?.(segs)}
                          style={{ display: "flex", gap: 6, fontSize: 10, color: "#9fb0d0", cursor: "crosshair", whiteSpace: "nowrap", padding: "0 2px" }}>
                          <span style={{ color: "#5b6b8c", width: 22 }}>#{i}</span>
                          <span style={{ color: "#c4ccdd" }}>{s.wormId}</span>
                          <span>{s.type}</span>
                          <span>sp{s.speed | 0}</span>
                          <span style={{ color: "#7fa0d8" }}>{(s.startX | 0)},{(s.startY | 0)}→{(s.endX | 0)},{(s.endY | 0)}</span>
                          {gap > 0.5 ? <span style={{ color: "#ff2fd0", fontWeight: 700 }}>gap{gap.toFixed(0)}</span> : null}
                        </div>
                      );
                    })}
                  </div>
                ) : <pre style={{ margin: 0, padding: "3px 6px", background: "#0a0e18", color: "#9fb0d0", fontSize: 10, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(e.msg, null, 1)}</pre>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Any coordinate posted in the info panel (a worm's pos, a death spot, an event) drops the
// canvas crosshair on hover. Wrap the text in this so every coordinate behaves the same way.
function HoverCoord({ x, y, owner, onHover, style, children }: { x?: number | null; y?: number | null; owner?: HoverOwner; onHover: (p: HoverPt | null) => void; style?: React.CSSProperties; children: React.ReactNode }) {
  const has = x != null && y != null;
  return (
    <div
      style={{ ...style, cursor: has ? "crosshair" : style?.cursor, borderRadius: 3 }}
      onMouseEnter={has ? () => onHover({ x: x as number, y: y as number, owner }) : undefined}
      onMouseLeave={has ? () => onHover(null) : undefined}
      onMouseOver={has ? (e) => ((e.currentTarget as HTMLElement).style.background = "rgba(57,255,20,0.12)") : undefined}
      onMouseOut={has ? (e) => ((e.currentTarget as HTMLElement).style.background = "transparent") : undefined}
    >{children}</div>
  );
}

// Pure renderer of an info snapshot. Used LIVE and in REPLAY (fed the recorded frame's
// snapshot), so the whole panel rewinds with the scrubber.
function InfoView({ snap, onHover }: { snap: InfoSnap | null; onHover: (p: HoverPt | null) => void }) {
  if (!snap) return <div style={{ flex: 1, minWidth: 340, maxWidth: 620 }} />;
  const th = (t: string) => <th style={{ ...ISUB, textAlign: "left", padding: "0 8px" }}>{t}</th>;
  const wdot = (c: string) => <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: c, marginRight: 5, verticalAlign: "middle" }} />;
  return (
    <div style={{ flex: 1, minWidth: 340, maxWidth: 620 }}>
      <div style={IBOX}>
        <div style={IHDR}>Diagnostics <span style={ISUB}>· lag = trails the server (game-s) · content = opponent copy vs server</span></div>
        <DiagTable rows={snap.diag} />
      </div>
      <div style={IBOX}>
        <div style={IHDR}>Worms per owner <span style={ISUB}>· live worms + their wall-hack mirrors (#N) · ❄ FROZEN / ⋯ SWITCHING per view · mirroring=head drawn / idle=none · ~=predicted</span></div>
        <div style={{ display: "flex", gap: 10 }}>
          {snap.worms.map((owner) => (
            <div key={owner.label} style={{ flex: 1, minWidth: 0 }}>
              <div style={{ ...ISUB, color: owner.accent, fontWeight: 700, marginBottom: 5, borderBottom: "1px solid #1e2636", paddingBottom: 3, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                <span>{owner.label} <span style={{ color: "#6b7590" }}>({owner.list.length})</span></span>
                {owner.switch && <span style={{ padding: "0 5px", borderRadius: 4, background: owner.switch === "frozen" ? "#2f7fd4" : "#8a6d3b", color: "#fff", fontSize: 9, fontWeight: 800, letterSpacing: 0.5 }}>{owner.switch === "frozen" ? "❄ FROZEN" : "⋯ SWITCHING"}</span>}
              </div>
              {owner.list.length === 0 ? <div style={ISUB}>-</div> : owner.list.map((w) => (
                <div key={w.id} style={{ ...IMONO, marginBottom: 4 }}>
                  <div>{wdot(w.color)}<span style={{ color: w.color, fontWeight: 700 }}>{w.id}</span></div>
                  <HoverCoord x={w.x} y={w.y} owner={owner.tag} onHover={onHover} style={{ color: "#c4ccdd", paddingLeft: 12 }}>{w.x != null ? `${w.x | 0},${w.y | 0}` : "-"}</HoverCoord>
                  {w.effects.map((e, j) => <div key={j} style={{ color: e.predicted ? "#8aa0ff" : "#8fe38a", paddingLeft: 12 }}>{e.predicted ? "~" : ""}{e.type} {e.t.toFixed(1)}s</div>)}
                  {w.ghosts.map((g) => (
                    <HoverCoord key={g.id} x={g.x} y={g.y} owner={owner.tag} onHover={onHover} style={{ paddingLeft: 12, color: FEED_COLOR.wrap }}>
                      <span style={{ color: FEED_COLOR.wrap }}>{g.id}</span> {g.x | 0},{g.y | 0}
                      <span style={{ color: g.active ? "#7CFC66" : "#6b7590", fontWeight: 700 }}> {g.active ? "mirroring" : "idle"}</span>
                    </HoverCoord>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div style={IBOX}>
        <div style={IHDR}>Segments <span style={ISUB}>· each worm's LATEST segment per view · sp=speed ln=this seg's length gap=jump from prev seg's end (⚠ discontinuity)</span></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          {snap.segs.map((sr) => (
            <div key={sr.id} style={{ ...IMONO, minWidth: 168 }}>
              <div>{wdot(sr.color)}<span style={{ color: sr.color, fontWeight: 700 }}>{sr.id}</span></div>
              {(["c1", "sv", "c2"] as HoverOwner[]).map((v, i) => {
                const s = sr.views[i];
                const label = v === "c1" ? "C1" : v === "sv" ? "SV" : "C2";
                if (!s) return <div key={v} style={{ paddingLeft: 12, color: "#4b556e" }}>{label} -</div>;
                const jump = s.gap > 5, bigLen = s.len > 25;
                return (
                  <div key={v} style={{ paddingLeft: 12, display: "flex", gap: 7 }}>
                    <span style={{ color: "#6b7590", width: 16 }}>{label}</span>
                    <span style={{ color: "#c4ccdd" }}>sp{s.speed | 0}</span>
                    <span style={{ color: bigLen ? "#ff9f45" : "#c4ccdd" }}>ln{s.len.toFixed(0)}</span>
                    <span style={{ color: jump ? "#ff2fd0" : "#6b7590", fontWeight: jump ? 700 : 400 }}>gap{s.gap.toFixed(0)}{jump ? "⚠" : ""}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div style={IBOX}>
        <div style={IHDR}>Deaths <span style={ISUB}>· client crash vs server kill (Δ = the retraction)</span></div>
        {snap.deaths.length === 0 ? <div style={ISUB}>none yet</div> : (
          <table style={{ borderCollapse: "collapse", ...IMONO, color: "#c4ccdd" }}>
            <thead><tr>{th("worm")}{th("server kill")}{th("client crash")}{th("Δ")}</tr></thead>
            <tbody>{snap.deaths.map((d) => {
              const dpx = (d.clX != null) ? Math.hypot(d.svX - (d.clX || 0), d.svY - (d.clY || 0)) : null;
              const dt = (d.clT != null) ? Math.abs(d.svT - d.clT) : null;
              const bad = (dpx != null && dpx > 15) || (dt != null && dt > 0.15);
              return (<tr key={`${d.key}@${d.svT}`}><td style={{ padding: "2px 8px" }}>{d.key.split("_").slice(1).join("_")}</td>
                <td style={{ padding: "2px 8px", cursor: "crosshair" }} onMouseEnter={() => onHover({ x: d.svX, y: d.svY, owner: "sv" })} onMouseLeave={() => onHover(null)}>{d.svT.toFixed(2)}s ({d.svX | 0},{d.svY | 0})</td>
                <td style={{ padding: "2px 8px", cursor: d.clX != null ? "crosshair" : undefined }} onMouseEnter={() => onHover(d.clX != null ? { x: d.clX, y: d.clY as number } : null)} onMouseLeave={() => onHover(null)}>{d.clT != null ? `${d.clT.toFixed(2)}s (${(d.clX || 0) | 0},${(d.clY || 0) | 0})` : "-"}</td>
                <td style={{ padding: "2px 8px", color: bad ? "#ff4d5e" : "#7CFC66", fontWeight: 700 }}>{dpx != null ? `${dpx | 0}px` : "-"}{dt != null ? ` ${dt.toFixed(2)}s` : ""}</td></tr>);
            })}</tbody>
          </table>
        )}
      </div>
      <div style={IBOX}>
        <div style={IHDR}>Events <span style={ISUB}>· newest first · <span style={{ color: "#4aa3ff" }}>●</span> C1 <span style={{ color: "#ff5c7a" }}>●</span> C2 · server = no dot</span></div>
        <div style={{ maxHeight: 300, overflowY: "auto" }}>
          {snap.events.length === 0 ? <div style={ISUB}>waiting…</div> : snap.events.slice(0, 40).map((f) => (
            <div key={f.id}
              onMouseEnter={() => onHover(f.x != null ? { x: f.x, y: f.y as number, owner: f.src } : null)}
              onMouseLeave={() => onHover(null)}
              style={{ ...IMONO, padding: "1px 4px", display: "flex", alignItems: "center", color: FEED_COLOR[f.kind], cursor: f.x != null ? "crosshair" : "default", borderRadius: 3 }}
              onMouseOver={(e) => { if (f.x != null) (e.currentTarget as HTMLElement).style.background = "rgba(124,252,102,0.12)"; }}
              onMouseOut={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
            >
              <span style={{ color: "#6b7590" }}>{f.t.toFixed(2)}s</span>
              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", margin: "0 6px", background: SRC_COLOR[f.src] || "transparent" }} />
              <span>{f.text}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={IBOX}>
        <div style={IHDR}>Network</div>
        <div style={{ ...IMONO, color: "#c4ccdd" }}>ping {snap.net.ping}ms · jitter {snap.net.jitter}ms · loss {Math.round(snap.net.loss * 100)}% · msgs {snap.net.msgs}</div>
      </div>
    </div>
  );
}

// Accumulates the event feed / deaths and builds an InfoSnap. Renders it live, and hands
// `snapRef` a fresh-snapshot getter so the recorder can capture the panel per frame.
function InfoPanel({ r, onHover, snapRef }: { r: { bus: any; server: any; c1: any; c2: any; players: any[] }; onHover: (p: HoverPt | null) => void; snapRef: React.MutableRefObject<(() => InfoSnap) | null> }) {
  const [, force] = useState(0);
  const feed = useRef<FeedItem[]>([]);
  const idc = useRef(0);
  const cur = useRef({ ev: 0, pu: 0, ef: 0, seq: 0 });
  const wrapFlag = useRef<Record<string, boolean>>({});
  const deaths = useRef<Record<string, DRow>>({});
  const lastHead = useRef<Record<string, { x: number; y: number }>>({}); // per view+worm, for teleport detection
  // A client can carry several local players (C1 = keyboard + a bot-steered local),
  // so map by the full local-id set, not just the primary.
  const c1Ids = r.c1.localIds(), c2Ids = r.c2.localIds();
  const ownerClient = (pid: string) => (c1Ids.indexOf(pid) !== -1 ? r.c1 : c2Ids.indexOf(pid) !== -1 ? r.c2 : null);
  const srcOf = (pid: string): FeedSrc => (c1Ids.indexOf(pid) !== -1 ? "c1" : c2Ids.indexOf(pid) !== -1 ? "c2" : "sv");

  // Every worm ALIVE in one owner's view, in that view's own order. A client-predicted
  // twin (local id like p0_ptwin0) shows only in C1's list until the server acknowledges
  // it and spawns its own (worm_N) - so comparing the three lists side by side makes the
  // twin id mismatch, and "C1 sees 3 worms, 2 blue + 1 red", obvious at a glance.
  const ownerList = (gs: any, client: any): OwnerWorm[] => {
    const predictors = client && client.predictors ? client.predictors() : null;
    return (gs?.worms || []).filter((w: any) => w.alive !== false).map((w: any) => {
      const pl = r.players.find((p) => p.id === w.playerId);
      const h = headOfWorm(gs, w.playerId, w.id);
      const predictor = predictors ? predictors[w.playerId] : null;
      return { id: w.id, color: pl ? wormColors[pl.colorId] : "#c4ccdd", x: h ? h.x : null, y: h ? h.y : null, effects: effectsOfWorm(gs, w.id), ghosts: ghostTracksOfWorm(gs, w.playerId, w.id, predictor) };
    });
  };
  const buildSnap = (): InfoSnap => {
    const sv = r.server.getServerGameState();
    const c1gs = r.c1.getGameState(), c2gs = r.c2.getGameState();
    const worms: OwnerList[] = [
      { label: "C1", tag: "c1", accent: "#4aa3ff", list: ownerList(c1gs, r.c1), switch: clientSwitchStatus(r.c1) },
      { label: "SV", tag: "sv", accent: "#7CFC66", list: ownerList(sv, null), switch: serverSwitchStatus(r.server) },
      { label: "C2", tag: "c2", accent: "#ff5c7a", list: ownerList(c2gs, r.c2), switch: clientSwitchStatus(r.c2) },
    ];
    const segIds = Array.from(new Set([...(c1gs?.worms || []), ...(sv?.worms || []), ...(c2gs?.worms || [])].map((w: any) => w.id)));
    const segs: SegRow[] = segIds.map((id) => {
      const svw = (sv?.worms || []).find((w: any) => w.id === id);
      const pl = svw ? r.players.find((p) => p.id === svw.playerId) : null;
      return { id, color: pl ? wormColors[pl.colorId] : "#c4ccdd", views: [latestSeg(c1gs, id, "c1"), latestSeg(sv, id, "sv"), latestSeg(c2gs, id, "c2")] };
    });
    return {
      diag: computeDiag(sv, c1gs, c2gs, r.players, c1Ids, c2Ids),
      worms,
      deaths: Object.entries(deaths.current).map(([key, d]) => ({ key, ...d })),
      events: feed.current.slice(0, 60),
      net: { ping: r.bus.conditions?.ping ?? 0, jitter: r.bus.conditions?.jitter ?? 0, loss: r.bus.conditions?.loss ?? 0, msgs: r.bus.getLog ? r.bus.getLog().length : 0 },
      segs,
    };
  };
  snapRef.current = buildSnap; // the recorder reads a fresh snapshot per captured frame

  useEffect(() => {
    const push = (t: number, kind: FeedKind, src: FeedSrc, text: string, x?: number, y?: number) => { feed.current.unshift({ id: idc.current++, t, kind, src, text, x, y }); if (feed.current.length > 120) feed.current.length = 120; };
    const timer = setInterval(() => {
      const sv = r.server.getServerGameState(); if (!sv) { force((n) => n + 1); return; }
      const puName: Record<string, string> = {};
      (sv.powerUpEvents || []).forEach((e: any) => { if (e.type === "spawn") puName[e.powerUp?.id] = e.powerUp?.name; });
      const ge = sv.gameEvents || [];
      for (let i = cur.current.ev; i < ge.length; i++) {
        const e = ge[i];
        if (e.type === "worm_died") {
          const svh = headOfWorm(sv, e.playerId, e.wormId);
          const oc = ownerClient(e.playerId);
          const clh = oc ? headOfWorm(oc.getGameState(), e.playerId, e.wormId) : null;
          const clT = oc && oc.ownCrashTime ? oc.ownCrashTime() : null;
          deaths.current[e.playerId + "_" + e.wormId] = { key: "", svT: e.time, svX: svh?.x ?? 0, svY: svh?.y ?? 0, clT: clT || null, clX: clh?.x ?? null, clY: clh?.y ?? null } as DRow;
          // Server-KILLED = the host's own collision killed it with no matching client crash report
          // (the owning client never said it crashed). That's the "server kills it at the wrong spot"
          // case: the worm dies where the client didn't expect, then the client snaps to the host.
          const logAll = r.bus.getLog ? r.bus.getLog() : [];
          const clientReported = logAll.some((mm: any) => mm.type === "worm_crashed" && mm.msg?.wormId === e.wormId);
          push(e.time, "death", srcOf(e.playerId), `${e.wormId} died  sv(${(svh?.x ?? 0) | 0},${(svh?.y ?? 0) | 0})${clientReported ? "" : "  [SERVER-killed]"}`, svh?.x, svh?.y);
        } else if (e.type === "victory_lap") {
          const who = r.players.find((pl) => pl.id === e.playerId);
          push(e.time, "lap", e.playerId ? srcOf(e.playerId) : "sv", `victory lap started${who ? " (" + who.name + ")" : ""}`);
        }
      }
      cur.current.ev = ge.length;
      const pe = sv.powerUpEvents || [];
      for (let i = cur.current.pu; i < pe.length; i++) { const e = pe[i]; if (e.type === "despawn") push(sv.gameTime || 0, "pickup", "sv", `pickup ${puName[e.id] || e.id}`); }
      cur.current.pu = pe.length;
      const ee = sv.effectEvents || [];
      for (let i = cur.current.ef; i < ee.length; i++) {
        const e = ee[i];
        if (e.type === "spawn") {
          const owner = (sv.worms || []).find((w: any) => w.id === e.effect?.wormId)?.playerId;
          push(sv.gameTime || 0, "effect", owner ? srcOf(owner) : "sv", `${e.effect?.type} on ${e.effect?.wormId}${e.effect?.duration ? " " + e.effect.duration.toFixed(1) + "s" : ""}`);
        } else if (e.type === "despawn") push(sv.gameTime || 0, "effect-end", "sv", `effect end #${e.effectId}`);
      }
      cur.current.ef = ee.length;
      const logArr = r.bus.getLog ? r.bus.getLog() : [];
      for (let i = cur.current.seq; i < logArr.length; i++) {
        const m = logArr[i];
        if (m.type === "worm_crashed") {
          // Where it crashed = the reporting client's view of that worm's head (the crash is
          // client-detected). Include the coords + make the row hoverable, like player deaths.
          const rc = m.from === "c1" ? r.c1 : m.from === "c2" ? r.c2 : null;
          const ch = rc ? headOfWorm(rc.getGameState(), "", m.msg?.wormId) : null;
          push((sv.gameTime || 0), "crash", m.from === "c1" || m.from === "c2" ? m.from : "sv", `${m.from} reports ${m.msg?.wormId} crashed${ch ? ` (${ch.x | 0},${ch.y | 0})` : ""}`, ch?.x, ch?.y);
        }
      }
      cur.current.seq = logArr.length;
      const mb = mapBounds(sv);
      if (mb) for (const w of sv.worms || []) {
        const h = headOfWorm(sv, w.playerId, w.id);
        const outside = !!h && (h.x < mb.x0 || h.x > mb.x1 || h.y < mb.y0 || h.y > mb.y1);
        const key = w.trackId || w.id;
        if (outside && !wrapFlag.current[key] && w.alive) push(sv.gameTime || 0, "wrap", srcOf(w.playerId), `${w.id} wraps at (${h!.x | 0},${h!.y | 0})`, h!.x, h!.y);
        wrapFlag.current[key] = outside;
      }
      // Teleport detection: any worm head that jumps > TELEPORT_PX in one sample, on EACH view.
      // A switcharoonie must NOT move a worm, so a jump here is a bug - and seeing which view jumps
      // first (SV = server, C1/C2 = clients) shows whether the server or a client caused it.
      const TELEPORT_PX = 35;
      const scanTp = (gs: any, view: string, src: FeedSrc) => {
        if (!gs) return;
        for (const w of gs.worms || []) {
          if (!w.alive) continue;
          const h = headOfWorm(gs, w.playerId, w.id);
          if (!h) continue;
          const key = view + ":" + w.id;
          const prev = lastHead.current[key];
          const wrapping = !!(mb && (h.x < mb.x0 || h.x > mb.x1 || h.y < mb.y0 || h.y > mb.y1));
          if (prev && !wrapping && Math.hypot(h.x - prev.x, h.y - prev.y) > TELEPORT_PX) {
            push(gs.gameTime || 0, "teleport", src, `${w.id} JUMP (${prev.x | 0},${prev.y | 0})->(${h.x | 0},${h.y | 0}) on ${view}`, h.x, h.y);
          }
          lastHead.current[key] = { x: h.x, y: h.y };
        }
      };
      scanTp(sv, "SV", "sv");
      scanTp(r.c1.getGameState(), "C1", "c1");
      scanTp(r.c2.getGameState(), "C2", "c2");
      force((n) => n + 1);
    }, 120);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <InfoView snap={buildSnap()} onHover={onHover} />;
}

function btn(accent?: string): React.CSSProperties {
  return { padding: "7px 14px", borderRadius: 8, border: `1px solid ${accent ? accent + "88" : "rgba(255,255,255,0.18)"}`, background: accent ? accent + "1f" : "rgba(255,255,255,0.05)", color: accent || "#cbd3e6", fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, cursor: "pointer" };
}
